// D5-02 + D2-14 (FE-5.4, FE-3.5): one shared, AFN-relevant starter-prompt list.
// Used by the MessageList empty state (suggestion chips) and the onboarding
// modal (first-message picker) so the two surfaces never drift apart.
import type { Agent } from '../types';

export type StarterPromptCategory = 'pipeline' | 'it' | 'general';

export interface StarterPrompt {
  id: string;
  /** Short label shown on chips/cards. */
  label: string;
  /** Full message sent to the agent when the prompt is chosen. */
  prompt: string;
  category: StarterPromptCategory;
}

export const AFN_STARTER_PROMPTS: StarterPrompt[] = [
  {
    id: 'pipeline-today',
    label: "What's on my pipeline today?",
    prompt: "What's on my pipeline today? Flag anything that needs my attention first.",
    category: 'pipeline',
  },
  {
    id: 'rate-lock-email',
    label: 'Draft a rate lock extension email',
    prompt: 'Help me draft a rate lock extension email I can send to a borrower whose lock is about to expire.',
    category: 'pipeline',
  },
  {
    id: 'it-help',
    label: 'Get help with an IT issue',
    prompt: "I'm stuck on an IT issue. Help me troubleshoot it, or point me to the right person to escalate to.",
    category: 'it',
  },
  {
    id: 'plan-day',
    label: 'Plan my day',
    prompt: 'Help me plan my day — what should I tackle first, and what can wait?',
    category: 'general',
  },
];

const IT_PATTERN = /\bit\b|help ?desk|infrastructure|network|password|hardware|software/i;
const PIPELINE_PATTERN = /mortgage|loan|pipeline|branch|lending|borrower|real estate|escrow|underwrit/i;

function agentHaystack(agent?: Pick<Agent, 'name' | 'description'> | null): string {
  if (!agent) {
    return '';
  }
  return `${agent.name || ''} ${agent.description || ''}`;
}

/**
 * Orders the shared list so the most relevant prompts for the agent come
 * first. Unknown/general agents get the full list in default order.
 */
export function getStarterPromptsForAgent(agent?: Pick<Agent, 'name' | 'description'> | null): StarterPrompt[] {
  const haystack = agentHaystack(agent);

  let preferred: StarterPromptCategory | null = null;
  if (IT_PATTERN.test(haystack)) {
    preferred = 'it';
  } else if (PIPELINE_PATTERN.test(haystack)) {
    preferred = 'pipeline';
  }

  if (!preferred) {
    return [...AFN_STARTER_PROMPTS];
  }

  return [
    ...AFN_STARTER_PROMPTS.filter((item) => item.category === preferred),
    ...AFN_STARTER_PROMPTS.filter((item) => item.category !== preferred),
  ];
}

export interface AgentWelcome {
  heading: string;
  subtext: string;
}

/**
 * D5-02 (FE-5.4): agent-aware welcome copy for the empty conversation state.
 */
export function getAgentWelcome(agent?: Pick<Agent, 'name' | 'description'> | null): AgentWelcome {
  const heading = 'What can I help with?';

  if (!agent?.name) {
    return { heading, subtext: 'Ask a question or pick a starter below. Your recent chats stay in the sidebar.' };
  }

  const haystack = agentHaystack(agent);
  if (IT_PATTERN.test(haystack)) {
    return { heading, subtext: `${agent.name} is ready to help with IT questions.` };
  }
  if (PIPELINE_PATTERN.test(haystack)) {
    return { heading, subtext: `${agent.name} is ready to help with your pipeline.` };
  }

  return { heading, subtext: `${agent.name} is ready to help — ask a question or pick a starter below.` };
}
